import {
  Box,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Tooltip,
} from "@chakra-ui/react";
import { NumberInput } from "@components/ui-kit";
import { NumberInputPercentStore } from "@components/ui-kit/number-input-percent/store";
import { t } from "@lingui/macro";
import type { FieldToNumberInputProps } from "@src/utils/forms/inputHelpers";
import { useStore } from "@src/utils/hooks";
import { observer, useLocalObservable } from "mobx-react-lite";
import {
  type ComponentProps,
  type CSSProperties,
  type ReactNode,
  useEffect,
} from "react";

interface PercentProps extends FieldToNumberInputProps {
  isDisabled?: boolean;
  disabledTooltip?: ReactNode;
  availableValue: number; // The value to calculate 100% from
}

interface NumberInputPercentProps extends ComponentProps<typeof NumberInput> {
  percentProps: PercentProps;
  wrapperStyle?: CSSProperties;
}

export const NumberInputPercent = observer(function NumberInputPercent({
  percentProps,
  onChange,
  error,
  wrapperStyle,
  ...props
}: NumberInputPercentProps) {
  const appStore = useStore();
  const store = useLocalObservable(
    () =>
      new NumberInputPercentStore(appStore, {
        onNumberInputChange: onChange,
        onPercentChange: percentProps.onChange,
      }),
  );

  useEffect(() => {
    // Whenever available value is changed, update the percent value
    store.updatePercentFromNumberValue(
      Number(props.value),
      percentProps.availableValue,
    );
  }, [
    store.updatePercentFromNumberValue,
    props.value,
    percentProps.availableValue,
  ]);

  const percentSlider = (
    <Box pos="absolute" bottom="-13.5px" w="full" px="5px">
      <Slider
        aria-label={t`Amount in percent`}
        focusThumbOnChange={false}
        isDisabled={
          props.disabled ||
          percentProps.isDisabled ||
          percentProps.availableValue <= 0
        }
        max={100}
        min={0}
        onChange={(value) =>
          store.handlePercentChange(
            value,
            percentProps.availableValue,
            props.value,
          )
        }
        step={1}
        value={
          props.disabled || percentProps.isDisabled
            ? 0
            : Number(percentProps.value) || 0
        }
      >
        <SliderTrack h="4px" bg="chakra-border-color._light">
          <SliderFilledTrack />
        </SliderTrack>
        <SliderThumb
          boxSize="18px"
          fontSize="11px"
          borderWidth="1px"
          borderColor="gray.300"
        >
          %
        </SliderThumb>
      </Slider>
    </Box>
  );

  return (
    <>
      <Box pos="relative" style={wrapperStyle}>
        <NumberInput
          {...props}
          error={store.isEnabled ? undefined : error}
          onChange={(value) =>
            store.handleNumberChange(value, percentProps.availableValue)
          }
        />

        {store.isEnabled &&
          (percentProps.isDisabled && !!percentProps.disabledTooltip ? (
            <Tooltip label={percentProps.disabledTooltip}>
              {percentSlider}
            </Tooltip>
          ) : (
            <Popover
              closeOnBlur={false}
              gutter={0}
              returnFocusOnClose={false}
              trigger="hover"
            >
              <PopoverTrigger>{percentSlider}</PopoverTrigger>
              <PopoverContent w="auto">
                <PopoverArrow />
                <PopoverBody px="2" py="2">
                  <Box w="24">
                    <NumberInput
                      minValue={0}
                      maxValue={100}
                      suffix="%"
                      disabled={
                        props.disabled ||
                        percentProps.isDisabled ||
                        percentProps.availableValue <= 0
                      }
                      value={percentProps.isDisabled ? "0" : percentProps.value}
                      onChange={(value) =>
                        store.handlePercentChange(
                          value,
                          percentProps.availableValue,
                          props.value,
                        )
                      }
                      decimalDigits={percentProps.decimalDigits}
                      error={percentProps.error}
                    />
                  </Box>
                </PopoverBody>
              </PopoverContent>
            </Popover>
          ))}
      </Box>

      {store.isEnabled && !!error && (
        <Box mt="2" textColor="red.500" fontSize="sm">
          {error}
        </Box>
      )}
    </>
  );
});
